/**
 * Injectable charting's pure rules (`BACKLOG.md` M9): the product-class taxonomy and the per-class
 * consent requirement — the fact, confirmed via AmSpa's Injectable Toolkit, that filler, Radiesse,
 * Sculptra, and a neuromodulator each need their own, genuinely separate consent document rather
 * than one generic "cosmetic procedure consent" (`../consentDocuments.ts`'s own header states the
 * general case; this file is the specific mapping for injectables).
 */

import { CONSENT_DOCUMENT_TYPES, type ConsentDocumentType } from '../consentDocuments.ts';

/**
 * Product classes. `filler` covers the broad hyaluronic-acid family (Juvederm, Restylane, etc.) —
 * clinically and consent-wise interchangeable enough to share one consent document, per AmSpa's own
 * toolkit; `radiesse` (calcium hydroxylapatite) and `sculptra` (poly-L-lactic acid) are named
 * separately because they are chemically distinct products with their own risk profiles and their
 * own AmSpa consent forms, not because they behave differently in this schema. `neuromodulator`
 * covers the whole Botox/Dysport/Xeomin/Jeuveau family under one consent, the same "clinically
 * interchangeable enough" reasoning as `filler`. `other` is the deliberate escape hatch for a product
 * this fixed set does not name — it has no required consent type (`requiredConsentTypeForProductClass`
 * returns `null`), which a route must treat as "fall back to `treatment_general`", not as "no
 * consent needed at all".
 */
export const INJECTABLE_PRODUCT_CLASSES = [
  'filler',
  'radiesse',
  'sculptra',
  'neuromodulator',
  'other',
] as const;
export type InjectableProductClass = (typeof INJECTABLE_PRODUCT_CLASSES)[number];

function includes(list: readonly string[], value: string): boolean {
  return list.includes(value);
}

export function isInjectableProductClass(value: string): value is InjectableProductClass {
  return includes(INJECTABLE_PRODUCT_CLASSES, value);
}

/**
 * Which `ConsentDocumentType` this product class requires on file. `null` for `other`, which the
 * caller (`server/src/aesthetics/injectableService.ts`) resolves to `treatment_general` — kept as an
 * explicit two-step (a `null` here, a fallback at the call site) rather than baking the fallback into
 * this table, so a reviewer diffing this table sees plainly that `other` has no dedicated document of
 * its own, rather than that choice being implicit in a shared constant.
 */
const REQUIRED_CONSENT_BY_PRODUCT_CLASS: Record<
  InjectableProductClass,
  ConsentDocumentType | null
> = {
  filler: 'cosmetic_filler',
  radiesse: 'cosmetic_radiesse',
  sculptra: 'cosmetic_sculptra',
  neuromodulator: 'cosmetic_neuromodulator',
  other: null,
};

export function requiredConsentTypeForProductClass(
  productClass: InjectableProductClass,
): ConsentDocumentType | null {
  return REQUIRED_CONSENT_BY_PRODUCT_CLASS[productClass];
}

/**
 * Every product class maps to a real member of `CONSENT_DOCUMENT_TYPES` or explicitly to `null` —
 * asserted here as a runtime invariant (not just by the `Record` type above) so a future edit to
 * either union that breaks the pairing fails a test immediately rather than only at typecheck time
 * for whoever next edits this specific file. Exported so `injectables.test.ts` can assert it holds
 * for every current and future entry without hand-listing them.
 */
export function everyRequiredConsentIsReal(): boolean {
  return INJECTABLE_PRODUCT_CLASSES.every((productClass) => {
    const required = REQUIRED_CONSENT_BY_PRODUCT_CLASS[productClass];
    return required === null || includes(CONSENT_DOCUMENT_TYPES, required);
  });
}

const PRODUCT_CLASS_LABELS: Record<InjectableProductClass, string> = {
  filler: 'Dermal filler',
  radiesse: 'Radiesse',
  sculptra: 'Sculptra',
  neuromodulator: 'Neuromodulator',
  other: 'Other injectable',
};

export function injectableProductClassLabel(value: string): string {
  return isInjectableProductClass(value) ? PRODUCT_CLASS_LABELS[value] : value;
}

/**
 * Is a lot number "on file" for this administration, in the sense the schema-level CHECK
 * (`aestheticsInjectableAdministrations`) enforces — either a real lot number, or an explicit,
 * recorded reason it is unavailable? This is the pure half of the "effectively mandatory" rule
 * `BACKLOG.md` M9 states: lot-number capture is not optional in practice (recall/traceability), but
 * an absolute NOT NULL would make a real, rare situation — a lot number genuinely lost or a product
 * used from an already-opened multi-use vial with no accessible label — unrepresentable rather than
 * documented. The database CHECK enforces the *shape* of this rule (one of the two columns is
 * non-null); this function is what a route or the UI calls to explain *why* a submission was
 * rejected, since a CHECK constraint violation alone reaches the caller as an opaque 500 rather than
 * a validation message — see `aestheticsRequests.ts`'s own `.refine` for where this is actually
 * enforced before the row ever reaches the database.
 */
export function hasLotNumberOnFile(input: {
  readonly lotNumber: string | null;
  readonly lotNumberUnavailableReason: string | null;
}): boolean {
  return input.lotNumber !== null || input.lotNumberUnavailableReason !== null;
}
